import type { SuggestedChange, SuggestedGraph } from '@/store';
import { normalizeTemplateForEntryKey, renderCellColumn } from '@/hooks';
import { GraphRefType, type Graph } from '../../../graph-editor/types';
import { TERMINAL_NODE_ID } from '../../../graph-editor/config/graph';
import { compileLogicTree } from '../../../graph-editor/lib/evaluate-graph';
import { createReplaceGraphCommand } from '../../../graph-editor/lib/commands';
import { graphParts, type Blocks } from './graph-change';

/** The graph Apply would build, made from a graph with only the terminal. */
const candidateGraph = (
  suggested: SuggestedGraph,
  blocks: Blocks,
  fieldId: string,
  current: Graph | undefined,
): Graph => {
  const empty: Graph = {
    id: `preview-${fieldId}`,
    ref: { kind: GraphRefType.Field, fieldId },
    graphVersion: 0,
    topologyVersion: 0,
    nodes: {
      [TERMINAL_NODE_ID]: {
        id: TERMINAL_NODE_ID,
        kind: 'terminal',
        options: {},
      },
    },
    edges: {},
  };
  const parts = graphParts(suggested, blocks, current);
  const result = createReplaceGraphCommand(parts).apply(empty);
  if (result.isErr()) throw new Error(result.error.message);
  return result.value.graph;
};

/**
 * The template a column or graph change gives the field, built the way the
 * builder builds it after Apply, or undefined when it can't be built. A graph
 * keeps the nodes of the field's current graph that the assistant keeps. A
 * value or an AI suggestion needs none: the card shows what it gives already.
 */
export const previewTemplate = (
  change: SuggestedChange,
  entryId: string,
  entryKey: string,
  blocks: Blocks,
  getGraphTemplate: (graphId: string) => unknown,
  current?: Graph,
): unknown => {
  if (change.kind === 'column') return renderCellColumn(change.value);
  if (change.kind !== 'graph') return undefined;
  try {
    const graph = candidateGraph(change.value, blocks, entryId, current);
    const compiled = compileLogicTree(
      graph,
      blocks,
      getGraphTemplate,
      TERMINAL_NODE_ID,
    );
    return normalizeTemplateForEntryKey(entryKey, compiled);
  } catch {
    return undefined;
  }
};
